import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Paper, Box } from '@mui/material';
import {
  formatRupeesIndianCompact,
  premiumTooltip,
  horizontalGrid,
  indianValueAxis,
  indianCategoryAxis,
  defaultAnimation,
} from './echartsConfig';
import { reportChartPaperSx } from './reportChartLayout';
import ReportCardHeader, { ReportInsightBanner } from './ReportCardHeader';
import ReportKpiStrip, { kpiMapToItems } from './ReportKpiStrip';

const toNum = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const norm = s => String(s || '').toLowerCase().trim();

const CONFIRMED_GREEN = '#66BB6A';

function prepareApcDisplay(data) {
  const rawLabels = (data?.labels || []).map(l => String(l ?? ''));
  const rawDatasets = data?.datasets || [];

  const confIdx = rawDatasets.findIndex(ds => norm(ds.label).includes('confirm'));
  const confDs = confIdx >= 0 ? rawDatasets[confIdx] : rawDatasets[0];
  if (!confDs) {
    return { labels: [], values: [] };
  }

  const confData = (confDs.data || []).map(toNum);

  const rows = rawLabels
    .map((label, i) => ({ label, value: confData[i] || 0 }))
    .filter(r => r.value > 0 && !norm(r.label).includes('total apc'))
    .sort((a, b) => b.value - a.value);

  if (!rows.length) {
    return { labels: [], values: [] };
  }

  return {
    labels: rows.map(r => r.label),
    values: rows.map(r => r.value),
  };
}

const VenueApcChart = ({ data, name, handleExport, showButton = true }) => {
  const displayName = data?.name || name;

  const prepared = useMemo(() => prepareApcDisplay(data), [data]);

  const plotHeight = useMemo(
    () => Math.min(440, Math.max(200, prepared.labels.length * 32)),
    [prepared.labels.length],
  );

  const option = useMemo(() => ({
    grid: horizontalGrid,
    tooltip: {
      ...premiumTooltip,
      formatter(params) {
        const p = Array.isArray(params) ? params[0] : params;
        if (!p) return '';
        return `<div style="font-weight:600;font-size:13px;color:#f8fafc;margin-bottom:4px">${p.name}</div>`
          + `<div style="font-variant-numeric:tabular-nums">APC: ${formatRupeesIndianCompact(p.value)}</div>`;
      },
    },
    xAxis: {
      ...indianValueAxis,
      position: 'bottom',
    },
    yAxis: {
      ...indianCategoryAxis,
      data: prepared.labels,
      inverse: true,
    },
    series: [
      {
        type: 'bar',
        data: prepared.values,
        barWidth: '55%',
        itemStyle: {
          color: CONFIRMED_GREEN,
          borderRadius: [0, 3, 3, 0],
        },
        emphasis: { itemStyle: { shadowBlur: 6, shadowColor: 'rgba(0,0,0,0.15)' } },
      },
    ],
    ...defaultAnimation,
  }), [prepared]);

  const apiKpis = kpiMapToItems(data?.kpiMap);
  const infoTooltip = data?.infoText;

  return (
    <Paper sx={reportChartPaperSx}>
      <ReportCardHeader
        title={displayName}
        insight={infoTooltip}
        handleExport={handleExport}
        showButton={showButton}
      />

      {apiKpis?.length > 0 && <ReportKpiStrip columns={2} items={apiKpis} />}

      <ReportInsightBanner text={data?.insightText} style={data?.insightStyle} />

      <Box sx={{ width: '100%', height: plotHeight }}>
        <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
      </Box>
    </Paper>
  );
};

export default VenueApcChart;
